import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Card,
  Grid,
  Heading,
  HStack,
  Text,
  VStack,
} from "components/ui";
import React, { useEffect, useState } from "react";
import { FaExchangeAlt, FaEye } from "react-icons/fa";
import { Checkbox } from "../../../../components/ui/checkbox";
import { Select } from "../../../../components/ui/select";
import { useTranslation } from "../../../../hooks/useTranslation";
import {
  ComparisonResult,
  FeatureFlag,
  featureFlagService,
  ToolPreviewResult,
} from "../../../../services/featureFlagService";
import {
  WidgetConfig,
  widgetService,
} from "../../../../services/widgetService";
import { showToast } from "../../../../utils/toast";

type ViewMode = "preview" | "compare";

const ToolPreviewPage: React.FC = () => {
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>("preview");
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [widgets, setWidgets] = useState<WidgetConfig[]>([]);

  // Single preview state
  const [selectedWidgetId, setSelectedWidgetId] = useState<string>("");
  const [selectedFlagKeys, setSelectedFlagKeys] = useState<string[]>([]);
  const [previewResult, setPreviewResult] = useState<ToolPreviewResult | null>(
    null,
  );
  const [previewLoading, setPreviewLoading] = useState(false);

  // Comparison state
  const [scenarioA, setScenarioA] = useState<{
    widgetId: string;
    flagKeys: string[];
  }>({
    widgetId: "",
    flagKeys: [],
  });
  const [scenarioB, setScenarioB] = useState<{
    widgetId: string;
    flagKeys: string[];
  }>({
    widgetId: "",
    flagKeys: [],
  });
  const [comparisonResult, setComparisonResult] =
    useState<ComparisonResult | null>(null);
  const [compareLoading, setCompareLoading] = useState(false);

  // Load flags and widgets
  useEffect(() => {
    const loadData = async () => {
      try {
        const [flagsData, widgetsData] = await Promise.all([
          featureFlagService.getActiveFlags(),
          widgetService.getAllWidgets(),
        ]);
        setFlags(flagsData);
        setWidgets(widgetsData);
      } catch (error: any) {
        showToast({
          title: "Error loading data",
          description: error.message,
          status: "error",
        });
      }
    };
    loadData();
  }, []);

  // Auto-trigger preview when widget or flags change
  useEffect(() => {
    if (!selectedWidgetId) {
      setPreviewResult(null);
      return;
    }

    const selectedWidget = widgets.find((w) => w.id === selectedWidgetId);
    if (
      !selectedWidget ||
      !selectedWidget.serverIds ||
      selectedWidget.serverIds.length === 0
    ) {
      setPreviewResult(null);
      return;
    }

    const fetchPreview = async () => {
      try {
        setPreviewLoading(true);
        const result = await featureFlagService.previewTools(
          selectedWidget.serverIds,
          selectedFlagKeys.length > 0 ? selectedFlagKeys : undefined,
        );
        setPreviewResult(result);
      } catch (error: any) {
        showToast({
          title: "Error previewing tools",
          description: error.response?.data?.message || error.message,
          status: "error",
        });
        setPreviewResult(null);
      } finally {
        setPreviewLoading(false);
      }
    };

    fetchPreview();
  }, [selectedWidgetId, selectedFlagKeys, widgets]);

  const handleCompare = async () => {
    if (!scenarioA.widgetId || !scenarioB.widgetId) {
      showToast({
        title: "Incomplete scenarios",
        description: "Both scenarios must have a widget selected",
        status: "warning",
      });
      return;
    }

    const widgetA = widgets.find((w) => w.id === scenarioA.widgetId);
    const widgetB = widgets.find((w) => w.id === scenarioB.widgetId);

    if (!widgetA?.serverIds?.length || !widgetB?.serverIds?.length) {
      showToast({
        title: "Invalid widgets",
        description: "Selected widgets must have servers configured",
        status: "warning",
      });
      return;
    }

    try {
      setCompareLoading(true);
      const result = await featureFlagService.compareToolsets(
        {
          serverIds: widgetA.serverIds,
          featureFlags:
            scenarioA.flagKeys.length > 0 ? scenarioA.flagKeys : undefined,
        },
        {
          serverIds: widgetB.serverIds,
          featureFlags:
            scenarioB.flagKeys.length > 0 ? scenarioB.flagKeys : undefined,
        },
      );
      setComparisonResult(result);
    } catch (error: any) {
      showToast({
        title: "Error comparing toolsets",
        description: error.response?.data?.message || error.message,
        status: "error",
      });
    } finally {
      setCompareLoading(false);
    }
  };

  const handleFlagToggle = (flagKey: string, checked: boolean) => {
    setSelectedFlagKeys((prev) =>
      checked ? [...prev, flagKey] : prev.filter((key) => key !== flagKey),
    );
  };

  const handleScenarioWidgetChange = (
    scenario: "A" | "B",
    widgetId: string,
  ) => {
    if (scenario === "A") {
      setScenarioA((prev) => ({ ...prev, widgetId }));
    } else {
      setScenarioB((prev) => ({ ...prev, widgetId }));
    }
  };

  const handleScenarioFlagToggle = (
    scenario: "A" | "B",
    flagKey: string,
    checked: boolean,
  ) => {
    if (scenario === "A") {
      setScenarioA((prev) => ({
        ...prev,
        flagKeys: checked
          ? [...prev.flagKeys, flagKey]
          : prev.flagKeys.filter((key) => key !== flagKey),
      }));
    } else {
      setScenarioB((prev) => ({
        ...prev,
        flagKeys: checked
          ? [...prev.flagKeys, flagKey]
          : prev.flagKeys.filter((key) => key !== flagKey),
      }));
    }
  };

  return (
    <Box p={6}>
      <VStack align="stretch" gap={6}>
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <Heading size="lg">{t("app.featureFlags.preview.title")}</Heading>
            <Text color="gray.600" mt={1}>
              {t("app.featureFlags.preview.description")}
            </Text>
          </Box>
          <HStack>
            <AskStrikeUpButton
              context={t("app.askStrikeUp.context.featureFlagsPreview")}
            />
            <Button
              variant={viewMode === "preview" ? "solid" : "outline"}
              colorPalette="blue"
              onClick={() => setViewMode("preview")}
            >
              <FaEye /> {t("app.featureFlags.preview.previewMode")}
            </Button>
            <Button
              variant={viewMode === "compare" ? "solid" : "outline"}
              colorPalette="blue"
              onClick={() => setViewMode("compare")}
            >
              <FaExchangeAlt /> {t("app.featureFlags.preview.compareMode")}
            </Button>
          </HStack>
        </HStack>

        {/* Preview Mode */}
        {viewMode === "preview" && (
          <Grid templateColumns="300px 1fr" gap={4}>
            {/* Configuration Panel */}
            <VStack align="stretch" gap={4}>
              <Card.Root>
                <Card.Body>
                  <VStack align="stretch" gap={4}>
                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        {t("app.featureFlags.preview.widget")}
                      </Text>
                      <Select
                        placeholder={t("app.featureFlags.preview.selectWidget")}
                        items={widgets.map((w) => ({
                          label: w.name,
                          value: w.id || "",
                        }))}
                        value={selectedWidgetId ? [selectedWidgetId] : []}
                        onValueChange={(e: any) =>
                          setSelectedWidgetId(e.value[0] || "")
                        }
                        size="sm"
                      />
                    </Box>

                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        {t("app.featureFlags.preview.accessPolicies")}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mb={2}>
                        {t(
                          "app.featureFlags.preview.selectPoliciesDescription",
                        )}
                      </Text>
                      <Box
                        maxH="400px"
                        overflowY="auto"
                        borderRadius="md"
                        p={2}
                      >
                        <VStack align="stretch" gap={2}>
                          {flags.map((flag) => (
                            <Checkbox
                              key={flag.key}
                              checked={selectedFlagKeys.includes(flag.key)}
                              onCheckedChange={(e: { checked: boolean }) =>
                                handleFlagToggle(flag.key, e.checked)
                              }
                            >
                              {flag.name}
                            </Checkbox>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </VStack>

            {/* Tools List */}
            <Box>
              {previewLoading ? (
                <Box p={6} borderRadius="md" textAlign="center">
                  <VStack gap={2}>
                    <Text color="gray.500" _dark={{ color: "gray.400" }}>
                      {t("app.featureFlags.preview.loadingTools")}
                    </Text>
                  </VStack>
                </Box>
              ) : previewResult ? (
                <Card.Root>
                  <Card.Header>
                    <HStack justify="space-between">
                      <Heading size="md">
                        {t("app.featureFlags.preview.availableTools")}
                      </Heading>
                      <Badge colorPalette="blue" size="lg">
                        {t("app.featureFlags.preview.toolsCount", {
                          count: previewResult.toolCount,
                        })}
                      </Badge>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <Box maxH="600px" overflowY="auto">
                      <VStack align="stretch" gap={3}>
                        {previewResult.tools.map((tool) => (
                          <Box
                            key={tool.id}
                            p={3}
                            borderRadius="md"
                            _hover={{
                              bg: "gray.50",
                              _dark: { bg: "gray.700" },
                            }}
                          >
                            <Text fontWeight="medium">{tool.name}</Text>
                            <Text
                              fontSize="sm"
                              color="gray.600"
                              _dark={{ color: "gray.400" }}
                              mt={1}
                            >
                              {tool.description}
                            </Text>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </Card.Body>
                </Card.Root>
              ) : (
                <Box p={6} borderRadius="md" textAlign="center">
                  <Text color="gray.500" _dark={{ color: "gray.400" }}>
                    {t("app.featureFlags.preview.selectWidgetPrompt")}
                  </Text>
                </Box>
              )}
            </Box>
          </Grid>
        )}

        {/* Compare Mode */}
        {viewMode === "compare" && (
          <VStack align="stretch" gap={6}>
            <Grid templateColumns="1fr 1fr" gap={6}>
              {/* Scenario A */}
              <Card.Root>
                <Card.Header>
                  <Heading size="md">
                    {t("app.featureFlags.preview.scenarioA")}
                  </Heading>
                </Card.Header>
                <Card.Body>
                  <VStack align="stretch" gap={4}>
                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        {t("app.featureFlags.preview.widget")}
                      </Text>
                      <Select
                        placeholder={t("app.featureFlags.preview.selectWidget")}
                        items={widgets.map((w) => ({
                          label: w.name,
                          value: w.id || "",
                        }))}
                        value={scenarioA.widgetId ? [scenarioA.widgetId] : []}
                        onValueChange={(e: any) =>
                          handleScenarioWidgetChange("A", e.value[0] || "")
                        }
                        size="sm"
                      />
                    </Box>

                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        {t("app.featureFlags.preview.featureFlags")}
                      </Text>
                      <Box
                        maxH="300px"
                        overflowY="auto"
                        borderRadius="md"
                        p={2}
                      >
                        <VStack align="stretch" gap={2}>
                          {flags.map((flag) => (
                            <Checkbox
                              key={flag.key}
                              checked={scenarioA.flagKeys.includes(flag.key)}
                              onCheckedChange={(e: { checked: boolean }) =>
                                handleScenarioFlagToggle(
                                  "A",
                                  flag.key,
                                  e.checked,
                                )
                              }
                            >
                              {flag.name}
                            </Checkbox>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  </VStack>
                </Card.Body>
              </Card.Root>

              {/* Scenario B */}
              <Card.Root>
                <Card.Header>
                  <Heading size="md">
                    {t("app.featureFlags.preview.scenarioB")}
                  </Heading>
                </Card.Header>
                <Card.Body>
                  <VStack align="stretch" gap={4}>
                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        {t("app.featureFlags.preview.widget")}
                      </Text>
                      <Select
                        placeholder={t("app.featureFlags.preview.selectWidget")}
                        items={widgets.map((w) => ({
                          label: w.name,
                          value: w.id || "",
                        }))}
                        value={scenarioB.widgetId ? [scenarioB.widgetId] : []}
                        onValueChange={(e: any) =>
                          handleScenarioWidgetChange("B", e.value[0] || "")
                        }
                        size="sm"
                      />
                    </Box>

                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        {t("app.featureFlags.preview.featureFlags")}
                      </Text>
                      <Box
                        maxH="300px"
                        overflowY="auto"
                        borderRadius="md"
                        p={2}
                      >
                        <VStack align="stretch" gap={2}>
                          {flags.map((flag) => (
                            <Checkbox
                              key={flag.key}
                              checked={scenarioB.flagKeys.includes(flag.key)}
                              onCheckedChange={(e: { checked: boolean }) =>
                                handleScenarioFlagToggle(
                                  "B",
                                  flag.key,
                                  e.checked,
                                )
                              }
                            >
                              {flag.name}
                            </Checkbox>
                          ))}
                        </VStack>
                      </Box>
                    </Box>
                  </VStack>
                </Card.Body>
              </Card.Root>
            </Grid>

            <Button
              colorPalette="blue"
              onClick={handleCompare}
              loading={compareLoading}
              alignSelf="center"
              width="300px"
            >
              {t("app.featureFlags.preview.compareResults")}
            </Button>

            {/* Comparison Results */}
            {comparisonResult && (
              <Grid templateColumns="1fr 1fr 1fr" gap={4}>
                {/* Only in A */}
                <Card.Root>
                  <Card.Header>
                    <HStack justify="space-between">
                      <Heading size="sm">
                        {t("app.featureFlags.preview.uniqueToScenarioA")}
                      </Heading>
                      <Badge colorPalette="blue">
                        {comparisonResult.comparison.onlyInA.length}
                      </Badge>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <Box maxH="400px" overflowY="auto">
                      <VStack align="stretch" gap={2}>
                        {comparisonResult.comparison.onlyInA.map((toolName) => (
                          <Text key={toolName} fontSize="sm">
                            {toolName}
                          </Text>
                        ))}
                        {comparisonResult.comparison.onlyInA.length === 0 && (
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            _dark={{ color: "gray.400" }}
                          >
                            {t("app.featureFlags.preview.noUniqueTools")}
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  </Card.Body>
                </Card.Root>

                {/* In Both */}
                <Card.Root>
                  <Card.Header>
                    <HStack justify="space-between">
                      <Heading size="sm">
                        {t("app.featureFlags.preview.inBothScenarios")}
                      </Heading>
                      <Badge colorPalette="green">
                        {comparisonResult.comparison.inBoth.length}
                      </Badge>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <Box maxH="400px" overflowY="auto">
                      <VStack align="stretch" gap={2}>
                        {comparisonResult.comparison.inBoth.map((toolName) => (
                          <Text key={toolName} fontSize="sm">
                            {toolName}
                          </Text>
                        ))}
                        {comparisonResult.comparison.inBoth.length === 0 && (
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            _dark={{ color: "gray.400" }}
                          >
                            {t("app.featureFlags.preview.noCommonTools")}
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  </Card.Body>
                </Card.Root>

                {/* Only in B */}
                <Card.Root>
                  <Card.Header>
                    <HStack justify="space-between">
                      <Heading size="sm">
                        {t("app.featureFlags.preview.uniqueToScenarioB")}
                      </Heading>
                      <Badge colorPalette="orange">
                        {comparisonResult.comparison.onlyInB.length}
                      </Badge>
                    </HStack>
                  </Card.Header>
                  <Card.Body>
                    <Box maxH="400px" overflowY="auto">
                      <VStack align="stretch" gap={2}>
                        {comparisonResult.comparison.onlyInB.map((toolName) => (
                          <Text key={toolName} fontSize="sm">
                            {toolName}
                          </Text>
                        ))}
                        {comparisonResult.comparison.onlyInB.length === 0 && (
                          <Text
                            fontSize="sm"
                            color="gray.500"
                            _dark={{ color: "gray.400" }}
                          >
                            {t("app.featureFlags.preview.noUniqueTools")}
                          </Text>
                        )}
                      </VStack>
                    </Box>
                  </Card.Body>
                </Card.Root>
              </Grid>
            )}
          </VStack>
        )}
      </VStack>
    </Box>
  );
};

export default ToolPreviewPage;
